import { AnnotationStrategy, ResizeHandle } from "./types";
import { v4 as uuidv4 } from "uuid"; // For generating unique IDs
import {
    colorWithAlpha,
    generateColorFromText,
    getContrastTextColor
} from "../../components/annotation/colors"; // Import color helpers
import { Point } from "@rapid/utils/scaleUtils";
import { renderMask } from "@rapid/utils/maskRenderer";

// --- Constants ---
const MIN_BOX_SIZE = 5; // Minimum width/height for a box
const RESIZE_HANDLE_SIZE = 8;
const RESIZE_HANDLE_CLICK_PADDING = 2;
const MIN_DRAG_DISTANCE_FOR_NEW_BOX = 5; // Minimum drag distance to create a new box
const LABEL_FONT = "11px monospace";
const LABEL_PADDING_X = 4; // Horizontal padding for label text
const LABEL_PADDING_Y_TOP = 2; // Vertical padding for label text (top)
const LABEL_PADDING_Y_BOTTOM = 4; // Vertical padding for label text (bottom) - Increased
const LABEL_CORNER_RADIUS = 4; // Radius for rounded corners of the label
const BOX_CORNER_RADIUS = 1; // Radius for rounded corners of the box
const LABEL_OFFSET_X_FOR_RADIUS = 0; // Small horizontal offset for the label due to box radius
const CORNER_REGION_MAX = 50;

// Delete button constants
const DELETE_BUTTON_RADIUS = 9;

// Visual constants for highlighting and drawing
const DEFAULT_STROKE_WIDTH = 1.5;
const HIGHLIGHT_STROKE_WIDTH = 2; // Slightly thicker for highlight
const HIGHLIGHT_FILL_ALPHA = 0.15; // Alpha for internal fill on highlight
const DEFAULT_LABEL_FALLBACK = "label"; // Fallback for generating color if label is empty

// Helper functions for label calculations
function measureTextDimensions(label: string): { width: number; height: number } {
    const tempCanvas = document.createElement("canvas");
    const tempCtx = tempCanvas.getContext("2d");
    if (!tempCtx) {
        return { width: 0, height: parseInt(LABEL_FONT, 10) || 11 };
    }

    tempCtx.font = LABEL_FONT;
    const textMetrics = tempCtx.measureText(label);
    const textWidth = textMetrics.width;
    const textHeight = (textMetrics as any).height || parseInt(LABEL_FONT, 10) || 11;

    return { width: textWidth, height: textHeight };
}

function calculateLabelBounds(
    label: string,
    tlx: number,
    tly: number
): { left: number; right: number; top: number; bottom: number } | null {
    if (!label) return null;

    const { width: textWidth, height: textHeight } = measureTextDimensions(label);

    const labelBgWidth = textWidth + LABEL_PADDING_X * 2;
    const labelBgHeight = textHeight + LABEL_PADDING_Y_TOP + LABEL_PADDING_Y_BOTTOM;
    const labelLeft = tlx + LABEL_OFFSET_X_FOR_RADIUS;
    const labelRight = labelLeft + labelBgWidth;
    const labelTop = tly - labelBgHeight;
    const labelBottom = tly; // Extend to connect with annotation box (no gap)

    return { left: labelLeft, right: labelRight, top: labelTop, bottom: labelBottom };
}

function isPointInBounds(
    point: { x: number; y: number },
    bounds: { left: number; right: number; top: number; bottom: number }
): boolean {
    return (
        point.x >= bounds.left &&
        point.x <= bounds.right &&
        point.y >= bounds.top &&
        point.y <= bounds.bottom
    );
}

export function getDeleteButtonCenter(annotation: {
    x: number;
    y: number;
    width: number;
    height: number;
    label?: string;
}): { cx: number; cy: number } {
    const tlx = annotation.x - annotation.width / 2;
    const brx = annotation.x + annotation.width / 2;
    const tly = annotation.y - annotation.height / 2;

    // Position outside the top-right corner of the box
    const offset = DELETE_BUTTON_RADIUS;
    let cx = brx + offset;
    let cy = tly - offset;

    // If the label extends past the box's right edge, push the button to the right of the label
    // and vertically center it with the label
    if (annotation.label) {
        const labelBounds = calculateLabelBounds(annotation.label, tlx, tly);
        if (labelBounds && labelBounds.right > cx - DELETE_BUTTON_RADIUS) {
            cx = labelBounds.right + DELETE_BUTTON_RADIUS + 2;
            cy = (labelBounds.top + labelBounds.bottom) / 2;
        }
    }

    return { cx, cy };
}

export interface BoundingBox {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    label: string;
    thumbnailDataUrl?: string;
    frameId?: string;
    color?: string;
    draft?: boolean;
    masks?: { counts: string; size: [number, number] };
    points?: Array<{ x: number; y: number }>;
    type?: "polygon" | "mask" | "box";
    maskLoading?: boolean;
    ephemeral?: boolean;
}

// --- BoundingBoxStrategy Implementation ---
export const BoundingBoxStrategyImpl: AnnotationStrategy<BoundingBox> = {
    draw: (ctx, annotation, options) => {
        const { x, y, width, height, label, color, draft, masks, points, maskLoading, ephemeral } =
            annotation;
        const { isSelected, isHovered, isLabelHovered, hideLabel } = options;

        const shouldDrawLabel = !ephemeral && label && (isSelected || isHovered || !hideLabel);

        const tlx = x - width / 2;
        const tly = y - height / 2;
        const brx = x + width / 2;
        const bry = y + height / 2;

        ctx.save();

        // const isDrawingPreviewFromCanvas = !!optionColor; // Removed

        // Render mask if available
        if (masks && masks.counts) {
            const baseColor = color || generateColorFromText(label || DEFAULT_LABEL_FALLBACK);
            if (ephemeral) {
                ctx.globalAlpha = 0.35;
            }
            renderMask(ctx, masks, x, y, width, height, baseColor, false);
            ctx.globalAlpha = 1;
        }

        // Determine the base color for this annotation
        const baseAnnotationColor = color || generateColorFromText(label || DEFAULT_LABEL_FALLBACK);

        if (!masks && points && points.length > 2) {
            ctx.beginPath();
            ctx.moveTo(points[0].x, points[0].y);
            points.slice(1).forEach((point) => ctx.lineTo(point.x, point.y));
            ctx.closePath();
            ctx.fillStyle = colorWithAlpha(ctx, baseAnnotationColor, ephemeral ? 0.2 : 0.25);
            ctx.fill();
            ctx.strokeStyle = baseAnnotationColor;
            ctx.lineWidth = isSelected || isHovered ? HIGHLIGHT_STROKE_WIDTH : DEFAULT_STROKE_WIDTH;
            ctx.stroke();
        }

        const currentStrokeColor = baseAnnotationColor;

        ctx.strokeStyle = currentStrokeColor;
        ctx.lineWidth = isSelected || isHovered ? HIGHLIGHT_STROKE_WIDTH : DEFAULT_STROKE_WIDTH;

        // Set dashed line for draft annotations
        if (maskLoading) {
            ctx.setLineDash([6, 6]);
            ctx.lineDashOffset = -((Date.now() / 80) % 12);
            ctx.globalAlpha = 0.85;
        } else if (ephemeral) {
            ctx.setLineDash([6, 5]);
            ctx.globalAlpha = 0.7;
        } else if (draft) {
            ctx.setLineDash([4, 4]); // 4px dash, 4px gap
        } else {
            ctx.setLineDash([]); // Solid line
        }

        // Draw the rounded rectangle path for the box
        // top left corner has no border radius if label is present and at top
        ctx.beginPath();
        if (!shouldDrawLabel) {
            ctx.moveTo(tlx + BOX_CORNER_RADIUS, tly);
        } else {
            ctx.moveTo(tlx, tly);
        }
        ctx.lineTo(brx - BOX_CORNER_RADIUS, tly);
        ctx.arcTo(brx, tly, brx, tly + BOX_CORNER_RADIUS, BOX_CORNER_RADIUS);
        ctx.lineTo(brx, bry - BOX_CORNER_RADIUS);
        ctx.arcTo(brx, bry, brx - BOX_CORNER_RADIUS, bry, BOX_CORNER_RADIUS);
        ctx.lineTo(tlx + BOX_CORNER_RADIUS, bry);
        ctx.arcTo(tlx, bry, tlx, bry - BOX_CORNER_RADIUS, BOX_CORNER_RADIUS);
        if (!shouldDrawLabel) {
            ctx.lineTo(tlx, tly + BOX_CORNER_RADIUS);
            ctx.arcTo(tlx, tly, tlx + BOX_CORNER_RADIUS, tly, BOX_CORNER_RADIUS);
        } else {
            ctx.lineTo(tlx, tly);
        }
        ctx.closePath();
        ctx.stroke();
        ctx.globalAlpha = 1;
        ctx.lineDashOffset = 0;

        if (maskLoading) {
            const spinnerRadius = 7;
            const spinnerX = brx - spinnerRadius - 4;
            const spinnerY = tly + spinnerRadius + 4;
            const rotation = Date.now() / 180;
            ctx.save();
            ctx.strokeStyle = baseAnnotationColor;
            ctx.lineWidth = 2;
            ctx.setLineDash([]);
            ctx.beginPath();
            ctx.arc(spinnerX, spinnerY, spinnerRadius, rotation, rotation + Math.PI * 1.4);
            ctx.stroke();
            ctx.restore();
        }

        // Apply internal fill only if selected (not for hover, since we now have dimming overlay)
        if (isSelected) {
            const fillColorSource = color || generateColorFromText(label || DEFAULT_LABEL_FALLBACK);
            ctx.fillStyle = colorWithAlpha(ctx, fillColorSource, HIGHLIGHT_FILL_ALPHA);
            ctx.fill();
        }

        // Draw the label
        if (shouldDrawLabel) {
            ctx.font = LABEL_FONT;

            // Measure text dimensions
            const { width: textWidth, height: textHeight } = measureTextDimensions(label);

            const labelBgHeight = textHeight + LABEL_PADDING_Y_TOP + LABEL_PADDING_Y_BOTTOM;
            const labelBgWidth = textWidth + LABEL_PADDING_X * 2;
            const labelBgTlx = tlx + LABEL_OFFSET_X_FOR_RADIUS;
            const labelBgTly = tly - labelBgHeight;

            // Label background matches the box stroke color exactly
            ctx.fillStyle = currentStrokeColor;

            // Apply glow effect when label is hovered
            if (isLabelHovered) {
                ctx.shadowColor = currentStrokeColor;
                ctx.shadowBlur = 20;
            }

            ctx.beginPath();
            ctx.moveTo(labelBgTlx + LABEL_CORNER_RADIUS, labelBgTly);
            ctx.lineTo(labelBgTlx + labelBgWidth - LABEL_CORNER_RADIUS, labelBgTly);
            ctx.arcTo(
                labelBgTlx + labelBgWidth,
                labelBgTly,
                labelBgTlx + labelBgWidth,
                labelBgTly + LABEL_CORNER_RADIUS,
                LABEL_CORNER_RADIUS
            );
            ctx.lineTo(labelBgTlx + labelBgWidth, labelBgTly + labelBgHeight);
            ctx.lineTo(labelBgTlx, labelBgTly + labelBgHeight);
            ctx.lineTo(labelBgTlx, labelBgTly + LABEL_CORNER_RADIUS);
            ctx.arcTo(
                labelBgTlx,
                labelBgTly,
                labelBgTlx + LABEL_CORNER_RADIUS,
                labelBgTly,
                LABEL_CORNER_RADIUS
            );
            ctx.closePath();
            ctx.fill();
            ctx.stroke();

            // Reset shadow after drawing background
            if (isLabelHovered) {
                ctx.shadowColor = "transparent";
                ctx.shadowBlur = 0;
            }

            ctx.fillStyle = getContrastTextColor(currentStrokeColor);
            ctx.fillText(
                label,
                tlx + LABEL_PADDING_X + LABEL_OFFSET_X_FOR_RADIUS,
                tly - LABEL_PADDING_Y_BOTTOM
            );
        }
        ctx.restore();
    },

    handleMouseDown: (point, image, currentAnnotation, allAnnotations, selectedLabel) => {
        // Priority: 1. Resize Handle, 2. Existing Annotation, 3. New Annotation
        if (currentAnnotation) {
            const resizeHandle = BoundingBoxStrategyImpl.getResizeHandleAtPoint(
                point,
                currentAnnotation
            );
            if (resizeHandle) {
                return {
                    drawingStateUpdate: {
                        action: "resizing",
                        annotation: currentAnnotation,
                        handle: resizeHandle,
                        startPoint: point,
                        originalAnnotation: { ...currentAnnotation } // Store original state
                    },
                    newAnnotation: { ...currentAnnotation }, // Add for immediate preview
                    stopPropagation: true,
                    isDragInitiation: true // It's a resize
                };
            }

            // Check if clicking on label area - if so, don't start moving
            const isLabelClick = BoundingBoxStrategyImpl.isPointInAnnotationLabel?.(
                point,
                currentAnnotation
            );

            if (isLabelClick) {
                // Clicked on label - don't start any drag operation
                return {
                    drawingStateUpdate: { action: "idle" },
                    stopPropagation: true,
                    isDragInitiation: false // Not a drag - it's a label click
                };
            }

            // Clicked on an existing annotation (not on a handle or label) -> start moving
            return {
                drawingStateUpdate: {
                    action: "moving",
                    annotation: currentAnnotation,
                    startPoint: point,
                    originalAnnotation: { ...currentAnnotation }
                },
                newAnnotation: { ...currentAnnotation }, // Add for immediate preview
                stopPropagation: true,
                isDragInitiation: true // It's a move
            };
        }

        // Clicked on empty space - start drawing new annotation if a label is selected
        if (selectedLabel) {
            return {
                drawingStateUpdate: {
                    action: "drawing",
                    startPoint: point,
                    currentPoint: point, // Initialize current point
                    label: selectedLabel
                },
                // Create a tiny initial preview annotation
                newAnnotation: {
                    id: uuidv4(),
                    x: point.x,
                    y: point.y,
                    width: MIN_BOX_SIZE / 2, // Start very small
                    height: MIN_BOX_SIZE / 2,
                    label: selectedLabel
                },
                stopPropagation: true,
                isDragInitiation: true // Drawing is also a drag initiation
            };
        }
        return { drawingStateUpdate: { action: "idle" }, isDragInitiation: false }; // Click on empty space with no label selected
    },

    handleMouseMove: (point, image, drawingState, allAnnotations, canvasGeom) => {
        if (drawingState.action === "idle") {
            let hovered: BoundingBox | null = null;
            let cursor = "crosshair";
            for (const anno of allAnnotations) {
                if (BoundingBoxStrategyImpl.isPointInAnnotation(point, anno)) {
                    hovered = anno;
                    cursor = "pointer";
                    if (BoundingBoxStrategyImpl.getResizeHandleAtPoint(point, anno)) {
                        cursor = "move";
                    }
                    break;
                }
            }
            return { hoveredAnnotation: hovered, cursor };
        }

        // Clamp point to canvas boundaries
        const clampedPoint = {
            x: Math.max(0, Math.min(point.x, canvasGeom.width)),
            y: Math.max(0, Math.min(point.y, canvasGeom.height))
        };

        if (drawingState.action === "drawing") {
            const { startPoint, label } = drawingState;
            // Use clampedPoint for current drawing position
            const newWidth = Math.abs(clampedPoint.x - startPoint.x);
            const newHeight = Math.abs(clampedPoint.y - startPoint.y);
            const newX = Math.min(startPoint.x, clampedPoint.x) + newWidth / 2;
            const newY = Math.min(startPoint.y, clampedPoint.y) + newHeight / 2;

            return {
                drawingStateUpdate: { currentPoint: clampedPoint }, // Store clamped point in state
                previewAnnotationUpdate: {
                    id: "preview-drawing",
                    x: newX,
                    y: newY,
                    width: Math.max(newWidth, MIN_BOX_SIZE),
                    height: Math.max(newHeight, MIN_BOX_SIZE),
                    label
                }
            };
        }

        if (drawingState.action === "resizing") {
            const { annotation, handle, startPoint, originalAnnotation } = drawingState;
            let { x, y, width, height } = originalAnnotation;

            // Calculate delta using the *original* un-clamped mouse point for consistent feel,
            // but apply clamping to the final coordinates and dimensions.
            const dx = point.x - startPoint.x;
            const dy = point.y - startPoint.y;

            let newX = x;
            let newY = y;
            let newWidth = width;
            let newHeight = height;

            const tlxOriginal = originalAnnotation.x - originalAnnotation.width / 2;
            const tlyOriginal = originalAnnotation.y - originalAnnotation.height / 2;
            const brxOriginal = originalAnnotation.x + originalAnnotation.width / 2;
            const bryOriginal = originalAnnotation.y + originalAnnotation.height / 2;

            // Tentative new edges based on mouse movement
            let tentativeTlx = tlxOriginal;
            let tentativeTly = tlyOriginal;
            let tentativeBrx = brxOriginal;
            let tentativeBry = bryOriginal;

            if (handle.includes("n")) tentativeTly += dy;
            if (handle.includes("s")) tentativeBry += dy;
            if (handle.includes("w")) tentativeTlx += dx;
            if (handle.includes("e")) tentativeBrx += dx;

            // Clamp edges to canvas boundaries
            tentativeTly = Math.max(0, Math.min(tentativeTly, canvasGeom.height));
            tentativeBry = Math.max(
                tentativeTly + MIN_BOX_SIZE,
                Math.min(tentativeBry, canvasGeom.height)
            ); // Ensure min height and within bounds
            if (handle.includes("n")) {
                tentativeBry = Math.max(
                    tentativeTly + MIN_BOX_SIZE,
                    bryOriginal + (handle.includes("s") ? dy : 0)
                );
                tentativeBry = Math.min(tentativeBry, canvasGeom.height);
            }
            if (handle.includes("s")) {
                tentativeTly = Math.min(
                    tentativeBry - MIN_BOX_SIZE,
                    tlyOriginal + (handle.includes("n") ? dy : 0)
                );
                tentativeTly = Math.max(0, tentativeTly);
            }

            tentativeTlx = Math.max(0, Math.min(tentativeTlx, canvasGeom.width));
            tentativeBrx = Math.max(
                tentativeTlx + MIN_BOX_SIZE,
                Math.min(tentativeBrx, canvasGeom.width)
            ); // Ensure min width and within bounds
            if (handle.includes("w")) {
                tentativeBrx = Math.max(
                    tentativeTlx + MIN_BOX_SIZE,
                    brxOriginal + (handle.includes("e") ? dx : 0)
                );
                tentativeBrx = Math.min(tentativeBrx, canvasGeom.width);
            }
            if (handle.includes("e")) {
                tentativeTlx = Math.min(
                    tentativeBrx - MIN_BOX_SIZE,
                    tlxOriginal + (handle.includes("w") ? dx : 0)
                );
                tentativeTlx = Math.max(0, tentativeTlx);
            }

            // Recalculate width, height, and center based on clamped edges
            newWidth = tentativeBrx - tentativeTlx;
            newHeight = tentativeBry - tentativeTly;
            newX = tentativeTlx + newWidth / 2;
            newY = tentativeTly + newHeight / 2;

            // Ensure dimensions are not less than MIN_BOX_SIZE (redundant if edge clamping is correct, but safe)
            newWidth = Math.max(newWidth, MIN_BOX_SIZE);
            newHeight = Math.max(newHeight, MIN_BOX_SIZE);

            return {
                previewAnnotationUpdate: {
                    ...originalAnnotation,
                    x: newX,
                    y: newY,
                    width: newWidth,
                    height: newHeight
                }
            };
        }

        if (drawingState.action === "moving") {
            const { startPoint, originalAnnotation } = drawingState;
            const dx = point.x - startPoint.x;
            const dy = point.y - startPoint.y;

            let newX = originalAnnotation.x + dx;
            let newY = originalAnnotation.y + dy;

            // Clamp the center such that the entire box remains within canvas boundaries
            const halfWidth = originalAnnotation.width / 2;
            const halfHeight = originalAnnotation.height / 2;

            newX = Math.max(halfWidth, Math.min(newX, canvasGeom.width - halfWidth));
            newY = Math.max(halfHeight, Math.min(newY, canvasGeom.height - halfHeight));

            return { previewAnnotationUpdate: { ...originalAnnotation, x: newX, y: newY } };
        }

        return {};
    },

    handleMouseUp: (point: Point, image, drawingState, selectedLabel) => {
        if (drawingState.action === "drawing") {
            // Cast to DrawingNewState to access startPoint and currentPoint
            const { startPoint, currentPoint, label } = drawingState as {
                action: "drawing";
                startPoint: Point;
                currentPoint: Point;
                label: string;
            };

            // Calculate dimensions and distance based on the start and (clamped) current points from drawingState
            const width = Math.abs(currentPoint.x - startPoint.x);
            const height = Math.abs(currentPoint.y - startPoint.y);
            const distance = Math.sqrt(
                Math.pow(currentPoint.x - startPoint.x, 2) +
                    Math.pow(currentPoint.y - startPoint.y, 2)
            );

            if (
                width < MIN_BOX_SIZE ||
                height < MIN_BOX_SIZE ||
                distance < MIN_DRAG_DISTANCE_FOR_NEW_BOX
            ) {
                // If too small or drag too short, signal no new annotation and reset to idle.
                return { drawingStateUpdate: { action: "idle" }, newAnnotation: null };
            }

            const newAnnotation: BoundingBox = {
                id: uuidv4(),
                x: Math.min(startPoint.x, currentPoint.x) + width / 2,
                y: Math.min(startPoint.y, currentPoint.y) + height / 2,
                width,
                height,
                label: label // Label from drawing state
            };
            return { newAnnotation, drawingStateUpdate: { action: "idle" } };
        }

        if (drawingState.action === "resizing" || drawingState.action === "moving") {
            // The previewAnnotation should hold the final state from mouseMove
            // The AnnotationCanvas will use this previewAnnotation to update the actual annotations array
            return {
                modifiedAnnotationId: drawingState.annotation.id,
                drawingStateUpdate: { action: "idle" }
            };
        }

        return { drawingStateUpdate: { action: "idle" } }; // Default reset
    },

    isPointInAnnotation: (point, annotation) => {
        const { id, x, y, width, height, label } = annotation; // Include id for logging
        const tlx = x - width / 2;
        const tly = y - height / 2;
        const brx = tlx + width;
        const bry = tly + height;

        // Check if point is in the main annotation box
        const inBox = isPointInBounds(point, { left: tlx, right: brx, top: tly, bottom: bry });

        // Also check if point is in the label area (if label exists)
        let inLabel = false;
        if (label) {
            const labelBounds = calculateLabelBounds(label, tlx, tly);
            if (labelBounds) {
                inLabel = isPointInBounds(point, labelBounds);
            }
        }

        const result = inBox || inLabel;

        // Log for ALL calls to isPointInAnnotation (commented out for performance)
        // console.log(
        //     `[BoundingBoxStrategy] isPointInAnnotation CALLED. ` +
        //     `Annotation ID: ${id}, ` +
        //     `point=(${point.x.toFixed(2)}, ${point.y.toFixed(2)}), ` +
        //     `boxTL=(${tlx.toFixed(2)}, ${tly.toFixed(2)}), ` +
        //     `boxDim=(${width.toFixed(2)}, ${height.toFixed(2)}), ` +
        //     `boxBR=(${brx.toFixed(2)}, ${bry.toFixed(2)}), inBox=${inBox}, inLabel=${inLabel}, result=${result}`
        // );
        return result;
    },

    isPointInAnnotationLabel: (point, annotation) => {
        const { x, y, width, height, label } = annotation;
        const tlx = x - width / 2;
        const tly = y - height / 2;

        if (!label) return false;

        const labelBounds = calculateLabelBounds(label, tlx, tly);
        if (!labelBounds) return false;

        return isPointInBounds(point, labelBounds);
    },

    getBoundsWithLabel: (annotation) => {
        const { x, y, width, height, label } = annotation;
        const tlx = x - width / 2;
        const tly = y - height / 2;

        // Start with the box bounds
        let minX = tlx;
        let minY = tly;
        let maxX = x + width / 2;
        let maxY = y + height / 2;

        // Extend to include label if present
        if (label) {
            const labelBounds = calculateLabelBounds(label, tlx, tly);
            if (labelBounds) {
                minX = Math.min(minX, labelBounds.left);
                minY = Math.min(minY, labelBounds.top);
                maxX = Math.max(maxX, labelBounds.right);
                maxY = Math.max(maxY, labelBounds.bottom);
            }
        }

        return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
    },

    getSvgCutoutPath: (annotation, scale = 1) => {
        const { x, y, width, height, label } = annotation;

        // Scale all coordinates
        const tlx = (x - width / 2) * scale;
        const tly = (y - height / 2) * scale;
        const brx = (x + width / 2) * scale;
        const bry = (y + height / 2) * scale;
        const boxRadius = BOX_CORNER_RADIUS * scale;
        const labelRadius = LABEL_CORNER_RADIUS * scale;

        // If no label, just draw a rounded rectangle for the box
        if (!label) {
            return `
                M ${tlx + boxRadius} ${tly}
                L ${brx - boxRadius} ${tly}
                A ${boxRadius} ${boxRadius} 0 0 1 ${brx} ${tly + boxRadius}
                L ${brx} ${bry - boxRadius}
                A ${boxRadius} ${boxRadius} 0 0 1 ${brx - boxRadius} ${bry}
                L ${tlx + boxRadius} ${bry}
                A ${boxRadius} ${boxRadius} 0 0 1 ${tlx} ${bry - boxRadius}
                L ${tlx} ${tly + boxRadius}
                A ${boxRadius} ${boxRadius} 0 0 1 ${tlx + boxRadius} ${tly}
                Z
            `;
        }

        // Calculate label dimensions (scaled)
        const { width: textWidth, height: textHeight } = measureTextDimensions(label);
        const labelBgHeight = (textHeight + LABEL_PADDING_Y_TOP + LABEL_PADDING_Y_BOTTOM) * scale;
        const labelBgWidth = (textWidth + LABEL_PADDING_X * 2) * scale;
        const labelBgTlx = tlx + LABEL_OFFSET_X_FOR_RADIUS * scale;
        const labelBgTly = tly - labelBgHeight;
        const labelBgBrx = labelBgTlx + labelBgWidth;

        // Create a combined path that traces the outline of both label and box
        // Starting from the top-left of the label, going clockwise
        return `
            M ${labelBgTlx + labelRadius} ${labelBgTly}
            L ${labelBgBrx - labelRadius} ${labelBgTly}
            A ${labelRadius} ${labelRadius} 0 0 1 ${labelBgBrx} ${labelBgTly + labelRadius}
            L ${labelBgBrx} ${tly}
            L ${brx - boxRadius} ${tly}
            A ${boxRadius} ${boxRadius} 0 0 1 ${brx} ${tly + boxRadius}
            L ${brx} ${bry - boxRadius}
            A ${boxRadius} ${boxRadius} 0 0 1 ${brx - boxRadius} ${bry}
            L ${tlx + boxRadius} ${bry}
            A ${boxRadius} ${boxRadius} 0 0 1 ${tlx} ${bry - boxRadius}
            L ${tlx} ${tly}
            L ${labelBgTlx} ${tly}
            L ${labelBgTlx} ${labelBgTly + labelRadius}
            A ${labelRadius} ${labelRadius} 0 0 1 ${labelBgTlx + labelRadius} ${labelBgTly}
            Z
        `;
    },

    getResizeHandleAtPoint: (point, annotation) => {
        const { x, y, width, height, label } = annotation;

        // First check if point is in label area - if so, don't allow resize handle detection
        if (label) {
            const tlx = x - width / 2;
            const tly = y - height / 2;
            const labelBounds = calculateLabelBounds(label, tlx, tly);

            if (labelBounds && isPointInBounds(point, labelBounds)) {
                return null; // No resize handle detection in label area
            }
        }

        const tlx = x - width / 2;
        const tly = y - height / 2;
        const brx = x + width / 2;
        const bry = y + height / 2;

        // How close to the edge to trigger resize
        const edgeThreshold = RESIZE_HANDLE_SIZE / 2 + RESIZE_HANDLE_CLICK_PADDING;

        // Distance from corner where diagonal resize is preferred
        const cornerRegionSize = Math.min(Math.min(width, height) * 0.33, CORNER_REGION_MAX);

        // Check if point is near any edge
        const nearLeft = Math.abs(point.x - tlx) <= edgeThreshold;
        const nearRight = Math.abs(point.x - brx) <= edgeThreshold;
        const nearTop = Math.abs(point.y - tly) <= edgeThreshold;
        const nearBottom = Math.abs(point.y - bry) <= edgeThreshold;
        const withinHorizontalSpan =
            point.x >= tlx - edgeThreshold && point.x <= brx + edgeThreshold;
        const withinVerticalSpan = point.y >= tly - edgeThreshold && point.y <= bry + edgeThreshold;

        // If not near any edge, no resize handle
        if (
            !(nearLeft && withinVerticalSpan) &&
            !(nearRight && withinVerticalSpan) &&
            !(nearTop && withinHorizontalSpan) &&
            !(nearBottom && withinHorizontalSpan)
        ) {
            return null;
        }

        // Check corners first
        if (nearLeft && nearTop && withinHorizontalSpan && withinVerticalSpan) {
            const distFromCorner = Math.sqrt(
                Math.pow(point.x - tlx, 2) + Math.pow(point.y - tly, 2)
            );
            if (distFromCorner <= cornerRegionSize) {
                return "nw";
            }
        }

        if (nearRight && nearTop && withinHorizontalSpan && withinVerticalSpan) {
            const distFromCorner = Math.sqrt(
                Math.pow(point.x - brx, 2) + Math.pow(point.y - tly, 2)
            );
            if (distFromCorner <= cornerRegionSize) {
                return "ne";
            }
        }

        if (nearLeft && nearBottom && withinHorizontalSpan && withinVerticalSpan) {
            const distFromCorner = Math.sqrt(
                Math.pow(point.x - tlx, 2) + Math.pow(point.y - bry, 2)
            );
            if (distFromCorner <= cornerRegionSize) {
                return "sw";
            }
        }

        if (nearRight && nearBottom && withinHorizontalSpan && withinVerticalSpan) {
            const distFromCorner = Math.sqrt(
                Math.pow(point.x - brx, 2) + Math.pow(point.y - bry, 2)
            );
            if (distFromCorner <= cornerRegionSize) {
                return "se";
            }
        }

        // If not in a corner region, check edges
        if (nearTop && withinHorizontalSpan) return "n";
        if (nearBottom && withinHorizontalSpan) return "s";
        if (nearLeft && withinVerticalSpan) return "w";
        if (nearRight && withinVerticalSpan) return "e";

        return null;
    },

    drawResizeHandles: (ctx, annotation) => {
        const { x, y, width, height, label, color } = annotation;
        const tlx = x - width / 2;
        const tly = y - height / 2;
        const brx = x + width / 2;
        const bry = y + height / 2;
        const midX = x;
        const midY = y;

        // Use the annotation's color (or generate from label) for the handles
        const handleColor = color || generateColorFromText(label || DEFAULT_LABEL_FALLBACK);

        const handles: { x: number; y: number; cursor: ResizeHandle }[] = [
            { x: tlx, y: tly, cursor: "nw" },
            { x: midX, y: tly, cursor: "n" },
            { x: brx, y: tly, cursor: "ne" },
            { x: tlx, y: midY, cursor: "w" },
            { x: brx, y: midY, cursor: "e" },
            { x: tlx, y: bry, cursor: "sw" },
            { x: midX, y: bry, cursor: "s" },
            { x: brx, y: bry, cursor: "se" }
        ];

        ctx.save();
        handles.forEach((handle) => {
            ctx.beginPath();
            ctx.arc(handle.x, handle.y, RESIZE_HANDLE_SIZE / 2, 0, 2 * Math.PI);
            ctx.fillStyle = handleColor;
            ctx.fill();
        });
        ctx.restore();
    }
};
